import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const EventsTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    nombre: "",
    sala: "",
    fecha: "",
    hora: "",
    fecha_hora_evento: "",
    direccion: "",
    flyer_url: "",
    max_tickets: 2000,
    activo: true,
    tramos_enabled: false,
    tramos_visible: true,
    tramos: [],
    pago_habilitado: false,
    precio: 0,
    // Mesas config
    mesas_enabled: false,
    mesas_cantidad: 0,
    mesas_consumicion_minima: 0,
    mesas_max_personas: 6,
    // Reservados config
    reservados_enabled: false,
    reservados_cantidad: 0,
    reservados_consumicion_minima: 0,
    reservados_max_personas: 6,
    // Bonocopas config - now with multiple types
    bonocopas_enabled: false,
    bonocopas_tipos: [],  // Array of {id, nombre, copas, precio, cantidad_disponible}
    // Email diario de aforo
    email_diario_enabled: false,
    email_diario_destinatario: "",
    email_diario_hora: "09:00"
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [flyerPreview, setFlyerPreview] = useState(null);
  const fileInputRef = useRef(null);
  const currentUploadIdRef = useRef(null);
  const [viewingReservations, setViewingReservations] = useState(null);
  const [reservationsList, setReservationsList] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [sendingDailyReport, setSendingDailyReport] = useState(false);

  const handleViewReservations = async (event) => {
    setViewingReservations(event);
    setLoadingReservations(true);
    try {
      const response = await axios.get(`${API}/admin/events/${event.id}/reservations`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setReservationsList(response.data);
    } catch (e) {
      console.error("Error fetching reservations:", e);
      setReservationsList([]);
    } finally {
      setLoadingReservations(false);
    }
  };

  const handleCloseReservations = () => {
    setViewingReservations(null);
    setReservationsList([]);
  };

  const handleEventFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : (name === "max_tickets" ? parseInt(value) || 0 : value)
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError("Formato no permitido. Usa: JPG, PNG, GIF o WebP");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("El archivo es demasiado grande (máx 5MB)");
      return;
    }

    // Generate unique upload ID to track this specific upload
    const uploadId = Date.now();
    currentUploadIdRef.current = uploadId;

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setFlyerPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data"
        }
      });

      // Only update if this upload wasn't cancelled
      if (response.data.success && currentUploadIdRef.current === uploadId) {
        // Check if URL is already absolute (Cloudinary) or relative (local)
        const uploadedUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${BACKEND_URL}${response.data.url}`;
        setEventForm(prev => ({ ...prev, flyer_url: uploadedUrl }));
      }
    } catch (e) {
      if (currentUploadIdRef.current === uploadId) {
        setError(e.response?.data?.detail || "Error al subir la imagen");
        setFlyerPreview(null);
      }
    } finally {
      if (currentUploadIdRef.current === uploadId) {
        setUploading(false);
      }
    }
  };

  const clearFlyer = () => {
    // Cancel any pending upload
    currentUploadIdRef.current = null;
    setUploading(false);
    setFlyerPreview(null);
    setEventForm(prev => ({ ...prev, flyer_url: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const config = {
        headers: { Authorization: `Basic ${credentials}` }
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventForm, config);
      } else {
        await axios.post(`${API}/events`, eventForm, config);
      }

      setShowForm(false);
      setEditingEvent(null);
      setFlyerPreview(null);
      setEventForm({
        nombre: "",
        sala: "",
        fecha: "",
        hora: "",
        fecha_hora_evento: "",
        direccion: "",
        flyer_url: "",
        max_tickets: 2000,
        activo: true,
        tramos_enabled: false,
        tramos_visible: true,
        tramos: [],
        email_diario_enabled: false,
        email_diario_destinatario: "",
        email_diario_hora: "09:00"
      });
      fetchEvents();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar el evento");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setEventForm({
      nombre: event.nombre,
      sala: event.sala,
      fecha: event.fecha,
      hora: event.hora,
      fecha_hora_evento: event.fecha_hora_evento || "",
      direccion: event.direccion,
      flyer_url: event.flyer_url || "",
      max_tickets: event.max_tickets,
      activo: event.activo,
      tramos_enabled: event.tramos_enabled || false,
      tramos_visible: event.tramos_visible !== false,
      tramos: event.tramos || [],
      pago_habilitado: event.pago_habilitado || false,
      precio: event.precio || 0,
      // Mesas config
      mesas_enabled: event.mesas_enabled || false,
      mesas_cantidad: event.mesas_cantidad || 0,
      mesas_consumicion_minima: event.mesas_consumicion_minima || 0,
      mesas_max_personas: event.mesas_max_personas || 6,
      // Reservados config
      reservados_enabled: event.reservados_enabled || false,
      reservados_cantidad: event.reservados_cantidad || 0,
      reservados_consumicion_minima: event.reservados_consumicion_minima || 0,
      reservados_max_personas: event.reservados_max_personas || 6,
      // Bonocopas config - multiple types
      bonocopas_enabled: event.bonocopas_enabled || false,
      bonocopas_tipos: event.bonocopas_tipos || [],
      // Email diario de aforo
      email_diario_enabled: event.email_diario_enabled || false,
      email_diario_destinatario: event.email_diario_destinatario || "",
      email_diario_hora: event.email_diario_hora || "09:00"
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("¿Estás seguro de eliminar este evento?")) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      fetchEvents();
    } catch (e) {
      alert("Error al eliminar el evento");
    }
  };

  // Scanner User handlers

  return (
    <>
          <>
            {/* Add Event Button - Hidden for gestors */}
            {!showForm && !isGestor && (
              <button 
                onClick={() => setShowForm(true)}
                className="btn-primary mb-8 w-auto px-6"
                data-testid="add-event-btn"
              >
                <Plus size={18} className="inline mr-2" />
                Nuevo Evento
              </button>
            )}

        {/* Event Form */}
        {showForm && (
          <div className="form-section mb-8 max-w-2xl">
            <h3 className="form-title mb-6">
              {editingEvent ? "Editar Evento" : "Nuevo Evento"}
            </h3>
            
            {error && <div className="error-message mb-4">{error}</div>}
            
            <form onSubmit={handleSubmitEvent}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Nombre del Evento</label>
                  <input
                    type="text"
                    name="nombre"
                    className="custom-input"
                    value={eventForm.nombre}
                    onChange={handleEventFormChange}
                    required
                    data-testid="event-nombre"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Sala / Lugar</label>
                  <input
                    type="text"
                    name="sala"
                    className="custom-input"
                    value={eventForm.sala}
                    onChange={handleEventFormChange}
                    required
                    data-testid="event-sala"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha</label>
                  <input
                    type="text"
                    name="fecha"
                    className="custom-input"
                    placeholder="Ej: 15 de Febrero 2025"
                    value={eventForm.fecha}
                    onChange={handleEventFormChange}
                    required
                    data-testid="event-fecha"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Hora</label>
                  <input
                    type="text"
                    name="hora"
                    className="custom-input"
                    placeholder="Ej: 23:00h"
                    value={eventForm.hora}
                    onChange={handleEventFormChange}
                    required
                    data-testid="event-hora"
                  />
                </div>
                <div className="form-group md:col-span-2">
                  <label className="form-label">Fecha y Hora exacta (para cierre de reservas)</label>
                  <input
                    type="datetime-local"
                    name="fecha_hora_evento"
                    className="custom-input"
                    value={eventForm.fecha_hora_evento}
                    onChange={handleEventFormChange}
                    data-testid="event-fecha-hora"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Las reservas se cerrarán 1 minuto antes de esta hora
                  </p>
                </div>
                <div className="form-group md:col-span-2">
                  <label className="form-label">Dirección</label>
                  <input
                    type="text"
                    name="direccion"
                    className="custom-input"
                    value={eventForm.direccion}
                    onChange={handleEventFormChange}
                    required
                    data-testid="event-direccion"
                  />
                </div>
                <div className="form-group md:col-span-2">
                  <label className="form-label">Flyer del Evento</label>
                  
                  {/* File Upload Area */}
                  <div className="flyer-upload-area">
                    {(flyerPreview || eventForm.flyer_url) ? (
                      <div className="flyer-preview-container">
                        <img 
                          src={flyerPreview || eventForm.flyer_url} 
                          alt="Preview" 
                          className="flyer-preview-img"
                        />
                        <button 
                          type="button"
                          onClick={clearFlyer}
                          className="flyer-remove-btn"
                          data-testid="remove-flyer-btn"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="flyer-upload-label" data-testid="flyer-upload-label">
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleFileUpload}
                          className="hidden"
                          data-testid="flyer-file-input"
                        />
                        {uploading ? (
                          <div className="text-center">
                            <span className="spinner"></span>
                            <p className="mt-2 text-sm">Subiendo...</p>
                          </div>
                        ) : (
                          <>
                            <Upload size={32} className="mx-auto mb-2 text-[var(--primary)]" />
                            <p className="text-sm text-[var(--text-muted)]">
                              Haz clic para subir una imagen
                            </p>
                            <p className="text-xs text-[var(--text-muted)] mt-1">
                              JPG, PNG, GIF o WebP (máx 5MB)
                            </p>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                  
                  {/* Alternative: URL input */}
                  <div className="mt-3">
                    <p className="text-xs text-[var(--text-muted)] mb-2">O pega una URL:</p>
                    <input
                      type="url"
                      name="flyer_url"
                      className="custom-input text-sm"
                      placeholder="https://ejemplo.com/imagen.jpg"
                      value={eventForm.flyer_url}
                      onChange={(e) => {
                        handleEventFormChange(e);
                        setFlyerPreview(null);
                      }}
                      data-testid="event-flyer-url"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Máximo de Entradas</label>
                  <input
                    type="number"
                    name="max_tickets"
                    className="custom-input"
                    min="1"
                    value={eventForm.max_tickets}
                    onChange={handleEventFormChange}
                    required
                    data-testid="event-max-tickets"
                  />
                </div>
                <div className="form-group flex items-center">
                  <input
                    type="checkbox"
                    name="activo"
                    id="activo"
                    className="custom-checkbox mr-3"
                    checked={eventForm.activo}
                    onChange={handleEventFormChange}
                    data-testid="event-activo"
                  />
                  <label htmlFor="activo" className="text-white cursor-pointer">
                    Evento Activo
                  </label>
                </div>
              </div>
              
              {/* Tramos Configuration */}
              <div className="mt-6 p-4 border border-[var(--border-color)] bg-[var(--surface)]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-bold uppercase text-sm">Configuración de Tramos</h4>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={eventForm.tramos_enabled}
                        onChange={(e) => setEventForm(prev => ({ ...prev, tramos_enabled: e.target.checked }))}
                      />
                      Activar Tramos
                    </label>
                    {eventForm.tramos_enabled && (
                      <label className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                        <input
                          type="checkbox"
                          className="custom-checkbox"
                          checked={eventForm.tramos_visible}
                          onChange={(e) => setEventForm(prev => ({ ...prev, tramos_visible: e.target.checked }))}
                        />
                        Mostrar al Cliente
                      </label>
                    )}
                  </div>
                </div>
                
                {eventForm.tramos_enabled && (
                  <>
                    <div className="space-y-2 mb-4">
                      {(eventForm.tramos || []).map((tramo, index) => (
                        <div key={index} className="flex items-center gap-3 bg-[var(--bg)] p-3">
                          <input
                            type="checkbox"
                            className="custom-checkbox"
                            checked={tramo.activo}
                            onChange={(e) => {
                              const newTramos = [...eventForm.tramos];
                              newTramos[index].activo = e.target.checked;
                              setEventForm(prev => ({ ...prev, tramos: newTramos }));
                            }}
                          />
                          <input
                            type="text"
                            className="custom-input flex-1"
                            placeholder="Nombre del tramo"
                            value={tramo.nombre}
                            onChange={(e) => {
                              const newTramos = [...eventForm.tramos];
                              newTramos[index].nombre = e.target.value;
                              setEventForm(prev => ({ ...prev, tramos: newTramos }));
                            }}
                          />
                          <input
                            type="number"
                            className="custom-input w-32"
                            placeholder="Cantidad"
                            min="1"
                            value={tramo.cantidad}
                            onChange={(e) => {
                              const newTramos = [...eventForm.tramos];
                              newTramos[index].cantidad = parseInt(e.target.value) || 0;
                              setEventForm(prev => ({ ...prev, tramos: newTramos }));
                            }}
                          />
                          <span className="text-[var(--text-muted)] text-sm">entradas</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newTramos = eventForm.tramos.filter((_, i) => i !== index);
                              setEventForm(prev => ({ ...prev, tramos: newTramos }));
                            }}
                            className="p-2 text-[var(--error)] hover:bg-[var(--error)] hover:text-white transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newTramos = [...(eventForm.tramos || []), { nombre: `Tramo ${(eventForm.tramos || []).length + 1}`, cantidad: 500, activo: true }];
                        setEventForm(prev => ({ ...prev, tramos: newTramos }));
                      }}
                      className="btn-download text-sm"
                    >
                      <Plus size={16} className="inline mr-2" />
                      Añadir Tramo
                    </button>
                    
                    {/* Botón descontar entradas - solo en modo edición */}
                    {editingEvent && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (window.confirm('¿Descontar 20 entradas del tramo activo?')) {
                            try {
                              const response = await axios.post(`${API}/events/${editingEvent.id}/tramos/descontar?cantidad=20`, {}, {
                                headers: { Authorization: `Basic ${credentials}` }
                              });
                              const data = response.data;
                              let msg = `✅ ${data.message}\n\nDisponibles en tramo: ${data.disponibles_restantes}`;
                              if (data.tramo_agotado) {
                                msg += `\n\n⚠️ ¡TRAMO AGOTADO!`;
                                if (data.siguiente_tramo_activado) {
                                  msg += `\n✅ Se ha activado: ${data.siguiente_tramo_activado}`;
                                }
                              }
                              alert(msg);
                              fetchEvents();
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Error al descontar entradas');
                            }
                          }
                        }}
                        className="btn-primary text-sm ml-4"
                        data-testid="descontar-btn"
                      >
                        <Users size={16} className="inline mr-2" />
                        Descontar 20 Entradas
                      </button>
                    )}
                  </>
                )}
              </div>
              
              {/* Payment Configuration */}
              <div className="mt-6 p-4 border border-[var(--border-color)] bg-[var(--surface)]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-bold uppercase text-sm">Configuración de Pago</h4>
                  <label className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                    <input
                      type="checkbox"
                      className="custom-checkbox"
                      checked={eventForm.pago_habilitado || false}
                      onChange={(e) => setEventForm(prev => ({ ...prev, pago_habilitado: e.target.checked }))}
                      data-testid="pago-habilitado"
                    />
                    Habilitar Pagos
                  </label>
                </div>
                
                {eventForm.pago_habilitado && (
                  <div className="flex items-center gap-3">
                    <label className="text-[var(--text-muted)] text-sm">Precio por entrada:</label>
                    <input
                      type="number"
                      className="custom-input w-32"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={eventForm.precio || ""}
                      onChange={(e) => setEventForm(prev => ({ ...prev, precio: parseFloat(e.target.value) || 0 }))}
                      data-testid="precio-entrada"
                    />
                    <span className="text-[var(--primary)] font-bold">€</span>
                    <span className="text-[var(--text-muted)] text-xs ml-2">
                      (Deja en 0 o vacío para entradas gratuitas)
                    </span>
                  </div>
                )}
                
                {!eventForm.pago_habilitado && (
                  <p className="text-[var(--text-muted)] text-sm">
                    Las entradas son gratuitas. Activa los pagos para cobrar por las entradas.
                  </p>
                )}
              </div>
              
              {/* Mesas Configuration */}
              <div className="mt-6 p-4 border border-[var(--border-color)] bg-[var(--surface)]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-bold uppercase text-sm">Configuración de Mesas</h4>
                  <label className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                    <input
                      type="checkbox"
                      className="custom-checkbox"
                      checked={eventForm.mesas_enabled || false}
                      onChange={(e) => setEventForm(prev => ({ ...prev, mesas_enabled: e.target.checked }))}
                      data-testid="mesas-habilitado"
                    />
                    Habilitar Mesas
                  </label>
                </div>
                
                {eventForm.mesas_enabled && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                      <label className="text-[var(--text-muted)] text-sm whitespace-nowrap">Nº mesas:</label>
                      <input
                        type="number"
                        className="custom-input w-20"
                        placeholder="0"
                        min="0"
                        value={eventForm.mesas_cantidad || ""}
                        onChange={(e) => setEventForm(prev => ({ ...prev, mesas_cantidad: parseInt(e.target.value) || 0 }))}
                        data-testid="mesas-cantidad"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-[var(--text-muted)] text-sm whitespace-nowrap">Consumición:</label>
                      <input
                        type="number"
                        className="custom-input w-20"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        value={eventForm.mesas_consumicion_minima || ""}
                        onChange={(e) => setEventForm(prev => ({ ...prev, mesas_consumicion_minima: parseFloat(e.target.value) || 0 }))}
                        data-testid="mesas-consumicion"
                      />
                      <span className="text-[var(--primary)] font-bold">€</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-[var(--text-muted)] text-sm whitespace-nowrap">Máx. personas:</label>
                      <input
                        type="number"
                        className="custom-input w-20"
                        placeholder="6"
                        min="1"
                        value={eventForm.mesas_max_personas || 6}
                        onChange={(e) => setEventForm(prev => ({ ...prev, mesas_max_personas: parseInt(e.target.value) || 6 }))}
                        data-testid="mesas-max-personas"
                      />
                    </div>
                  </div>
                )}
                
                {!eventForm.mesas_enabled && (
                  <p className="text-[var(--text-muted)] text-sm">
                    Activa las mesas para permitir reservas de mesa con consumición mínima.
                  </p>
                )}
              </div>
              
              {/* Reservados Configuration */}
              <div className="mt-6 p-4 border border-[var(--border-color)] bg-[var(--surface)]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-bold uppercase text-sm">Configuración de Reservados</h4>
                  <label className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                    <input
                      type="checkbox"
                      className="custom-checkbox"
                      checked={eventForm.reservados_enabled || false}
                      onChange={(e) => setEventForm(prev => ({ ...prev, reservados_enabled: e.target.checked }))}
                      data-testid="reservados-habilitado"
                    />
                    Habilitar Reservados
                  </label>
                </div>
                
                {eventForm.reservados_enabled && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                      <label className="text-[var(--text-muted)] text-sm whitespace-nowrap">Nº reservados:</label>
                      <input
                        type="number"
                        className="custom-input w-20"
                        placeholder="0"
                        min="0"
                        value={eventForm.reservados_cantidad || ""}
                        onChange={(e) => setEventForm(prev => ({ ...prev, reservados_cantidad: parseInt(e.target.value) || 0 }))}
                        data-testid="reservados-cantidad"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-[var(--text-muted)] text-sm whitespace-nowrap">Consumición:</label>
                      <input
                        type="number"
                        className="custom-input w-20"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        value={eventForm.reservados_consumicion_minima || ""}
                        onChange={(e) => setEventForm(prev => ({ ...prev, reservados_consumicion_minima: parseFloat(e.target.value) || 0 }))}
                        data-testid="reservados-consumicion"
                      />
                      <span className="text-[var(--primary)] font-bold">€</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-[var(--text-muted)] text-sm whitespace-nowrap">Máx. personas:</label>
                      <input
                        type="number"
                        className="custom-input w-20"
                        placeholder="6"
                        min="1"
                        value={eventForm.reservados_max_personas || 6}
                        onChange={(e) => setEventForm(prev => ({ ...prev, reservados_max_personas: parseInt(e.target.value) || 6 }))}
                        data-testid="reservados-max-personas"
                      />
                    </div>
                  </div>
                )}
                
                {!eventForm.reservados_enabled && (
                  <p className="text-[var(--text-muted)] text-sm">
                    Activa los reservados VIP para permitir reservas con consumición mínima.
                  </p>
                )}
              </div>

              {/* Bonocopas Config Section - Multiple Types */}
              <div className="bg-[var(--surface-highlight)] p-4 border border-yellow-500/30 mt-4">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="bonocopas_enabled"
                    checked={eventForm.bonocopas_enabled || false}
                    onChange={(e) => setEventForm(prev => ({ ...prev, bonocopas_enabled: e.target.checked }))}
                    className="w-5 h-5 accent-yellow-500"
                    data-testid="bonocopas-enabled"
                  />
                  <label htmlFor="bonocopas_enabled" className="text-yellow-500 font-bold uppercase cursor-pointer">
                    🍸 Bonocopas
                  </label>
                </div>
                
                {eventForm.bonocopas_enabled && (
                  <div className="mt-4 space-y-4">
                    <p className="text-[var(--text-muted)] text-sm mb-3">
                      Configura diferentes tipos de bonocopas con distintas cantidades de copas y precios.
                    </p>
                    
                    {/* List of bonocopa types */}
                    {(eventForm.bonocopas_tipos || []).map((tipo, index) => (
                      <div key={tipo.id || index} className="bg-black/30 p-3 border border-yellow-500/20 rounded flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[150px]">
                          <label className="text-xs text-[var(--text-muted)]">Nombre</label>
                          <input
                            type="text"
                            className="custom-input text-sm"
                            placeholder="Ej: Bonocopa Básico"
                            value={tipo.nombre || ""}
                            onChange={(e) => {
                              const newTipos = [...(eventForm.bonocopas_tipos || [])];
                              newTipos[index] = { ...newTipos[index], nombre: e.target.value };
                              setEventForm(prev => ({ ...prev, bonocopas_tipos: newTipos }));
                            }}
                          />
                        </div>
                        <div className="w-20">
                          <label className="text-xs text-[var(--text-muted)]">Copas</label>
                          <input
                            type="number"
                            className="custom-input text-sm"
                            placeholder="5"
                            min="1"
                            max="20"
                            value={tipo.copas || ""}
                            onChange={(e) => {
                              const newTipos = [...(eventForm.bonocopas_tipos || [])];
                              newTipos[index] = { ...newTipos[index], copas: parseInt(e.target.value) || 0 };
                              setEventForm(prev => ({ ...prev, bonocopas_tipos: newTipos }));
                            }}
                          />
                        </div>
                        <div className="w-24">
                          <label className="text-xs text-[var(--text-muted)]">Precio €</label>
                          <input
                            type="number"
                            className="custom-input text-sm"
                            placeholder="25.00"
                            min="0"
                            step="0.01"
                            value={tipo.precio || ""}
                            onChange={(e) => {
                              const newTipos = [...(eventForm.bonocopas_tipos || [])];
                              newTipos[index] = { ...newTipos[index], precio: parseFloat(e.target.value) || 0 };
                              setEventForm(prev => ({ ...prev, bonocopas_tipos: newTipos }));
                            }}
                          />
                        </div>
                        <div className="w-24">
                          <label className="text-xs text-[var(--text-muted)]">Cantidad</label>
                          <input
                            type="number"
                            className="custom-input text-sm"
                            placeholder="50"
                            min="1"
                            value={tipo.cantidad_disponible || ""}
                            onChange={(e) => {
                              const newTipos = [...(eventForm.bonocopas_tipos || [])];
                              newTipos[index] = { ...newTipos[index], cantidad_disponible: parseInt(e.target.value) || 0 };
                              setEventForm(prev => ({ ...prev, bonocopas_tipos: newTipos }));
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newTipos = (eventForm.bonocopas_tipos || []).filter((_, i) => i !== index);
                            setEventForm(prev => ({ ...prev, bonocopas_tipos: newTipos }));
                          }}
                          className="text-red-500 hover:text-red-400 p-2"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                    
                    {/* Add new type button */}
                    <button
                      type="button"
                      onClick={() => {
                        const newTipo = {
                          id: `tipo_${Date.now()}`,
                          nombre: "",
                          copas: 5,
                          precio: 0,
                          cantidad_disponible: 50
                        };
                        setEventForm(prev => ({ 
                          ...prev, 
                          bonocopas_tipos: [...(prev.bonocopas_tipos || []), newTipo] 
                        }));
                      }}
                      className="w-full py-2 border-2 border-dashed border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus size={18} />
                      Añadir tipo de Bonocopa
                    </button>
                    
                    <p className="text-xs text-red-400 mt-2">
                      *Los bonocopas no incluyen bebidas premium ni champagne
                    </p>
                  </div>
                )}
                
                {!eventForm.bonocopas_enabled && (
                  <p className="text-[var(--text-muted)] text-sm">
                    Activa los bonocopas para vender bonos de consumición anticipada.
                  </p>
                )}
              </div>
              
              {/* Email Diario de Aforo */}
              <div className="form-section mt-6">
                <h3 className="font-heading text-lg text-[var(--accent-cyan)] mb-4 uppercase flex items-center gap-2">
                  <Mail size={20} />
                  Email Diario de Aforo
                </h3>
                
                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={eventForm.email_diario_enabled}
                    onChange={(e) => setEventForm(prev => ({ ...prev, email_diario_enabled: e.target.checked }))}
                    className="w-5 h-5 accent-[var(--accent-cyan)]"
                    data-testid="email-diario-enabled"
                  />
                  <span className="text-white">Activar reporte diario de aforo por email</span>
                </label>
                
                {eventForm.email_diario_enabled && (
                  <div className="space-y-4 pl-4 border-l-2 border-[var(--accent-cyan)]">
                    <div className="form-group">
                      <label className="form-label">Emails de destinatarios *</label>
                      <input
                        type="text"
                        className="custom-input"
                        placeholder="admin@ejemplo.com, gerente@ejemplo.com, marketing@ejemplo.com"
                        value={eventForm.email_diario_destinatario}
                        onChange={(e) => setEventForm(prev => ({ ...prev, email_diario_destinatario: e.target.value }))}
                        required={eventForm.email_diario_enabled}
                        data-testid="email-diario-destinatario"
                      />
                      <p className="text-[var(--text-muted)] text-xs mt-1">
                        📧 Puedes añadir varios correos separados por comas
                      </p>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Hora de envío</label>
                      <select
                        className="custom-input"
                        value={eventForm.email_diario_hora}
                        onChange={(e) => setEventForm(prev => ({ ...prev, email_diario_hora: e.target.value }))}
                        data-testid="email-diario-hora"
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i.toString().padStart(2, '0') + ':00';
                          return <option key={hour} value={hour}>{hour}</option>;
                        })}
                      </select>
                      <p className="text-[var(--text-muted)] text-xs mt-1">
                        Hora a la que se enviará el reporte diario
                      </p>
                    </div>
                    
                    <div className="bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)]/30 p-3 rounded">
                      <p className="text-[var(--accent-cyan)] text-sm">
                        📊 Recibirás un email diario con:
                      </p>
                      <ul className="text-[var(--text-muted)] text-xs mt-2 list-disc list-inside space-y-1">
                        <li>Total de entradas adquiridas y validadas</li>
                        <li>Porcentaje de ocupación</li>
                        <li>Distribución por género</li>
                        <li>Top 5 procedencias de asistentes</li>
                        <li>Detalle por tramos (si aplica)</li>
                      </ul>
                    </div>
                    
                    {/* Botón Enviar Ahora - solo visible al editar un evento existente */}
                    {editingEvent && eventForm.email_diario_destinatario && (
                      <button
                        type="button"
                        disabled={sendingDailyReport}
                        onClick={async () => {
                          setSendingDailyReport(true);
                          try {
                            const config = { headers: { Authorization: `Basic ${credentials}` } };
                            await axios.post(`${API}/admin/events/${editingEvent.id}/send-daily-report`, {}, config);
                            const numEmails = eventForm.email_diario_destinatario.split(',').filter(e => e.trim()).length;
                            alert(`✅ Reporte enviado correctamente a ${numEmails} destinatario(s)`);
                          } catch (e) {
                            alert(`❌ Error al enviar: ${e.response?.data?.detail || e.message}`);
                          } finally {
                            setSendingDailyReport(false);
                          }
                        }}
                        className="w-full mt-3 py-3 bg-[var(--accent-cyan)] text-black font-bold hover:bg-[var(--accent-cyan)]/80 transition-colors flex items-center justify-center gap-2"
                        data-testid="send-daily-report-btn"
                      >
                        {sendingDailyReport ? (
                          <>
                            <span className="spinner-small"></span>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Mail size={18} />
                            Enviar Reporte Ahora
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
                
                {!eventForm.email_diario_enabled && (
                  <p className="text-[var(--text-muted)] text-sm">
                    Activa esta opción para recibir un email diario con las estadísticas del evento.
                  </p>
                )}
              </div>
              
              <div className="flex gap-4 mt-6">
                <button type="submit" className="btn-primary" disabled={loading || uploading} data-testid="event-submit-btn">
                  {loading ? <span className="spinner"></span> : (editingEvent ? "Guardar Cambios" : "Crear Evento")}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowForm(false);
                    setEditingEvent(null);
                    setFlyerPreview(null);
                    setEventForm({
                      nombre: "",
                      sala: "",
                      fecha: "",
                      hora: "",
                      direccion: "",
                      flyer_url: "",
                      max_tickets: 2000,
                      activo: true,
                      pago_habilitado: false,
                      precio: 0,
                      mesas_enabled: false,
                      mesas_cantidad: 0,
                      mesas_consumicion_minima: 0,
                      mesas_max_personas: 6,
                      reservados_enabled: false,
                      reservados_cantidad: 0,
                      reservados_consumicion_minima: 0,
                      reservados_max_personas: 6,
                      bonocopas_enabled: false,
                      bonocopas_tipos: [],
                      email_diario_enabled: false,
                      email_diario_destinatario: "",
                      email_diario_hora: "09:00"
                    });
                  }}
                  className="btn-download"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Events List */}
        <div className="admin-events-list">
          <h3 className="font-heading text-xl text-white mb-4 uppercase">
            Eventos ({events.length})
            {isGestor && <span className="text-sm text-[var(--text-muted)] ml-2 font-normal normal-case">(asignados)</span>}
          </h3>
          
          {events.length === 0 ? (
            <div className="text-center py-8">
              {isGestor ? (
                <div className="bg-orange-500/10 border border-orange-500/30 p-6">
                  <AlertTriangle size={32} className="mx-auto text-orange-400 mb-3" />
                  <p className="text-orange-400 font-bold mb-2">No tienes eventos asignados</p>
                  <p className="text-[var(--text-muted)] text-sm">
                    Contacta con el administrador para que te asigne eventos.
                  </p>
                </div>
              ) : (
                <p className="text-[var(--text-muted)]">No hay eventos creados</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {events.map(event => (
                <div 
                  key={event.id} 
                  className={`admin-event-card ${!event.activo ? 'opacity-50' : ''}`}
                  data-testid={`admin-event-${event.id}`}
                >
                  <div className="flex gap-4">
                    {event.flyer_url && (
                      <div className="w-20 flex-shrink-0" style={{ aspectRatio: '3/4' }}>
                        <img 
                          src={event.flyer_url} 
                          alt={event.nombre}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    )}
                    <div className="flex-grow">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-white font-bold text-lg">{event.nombre}</h4>
                          <p className="text-[var(--text-muted)] text-sm">
                            {event.sala} • {event.fecha} • {event.hora}
                          </p>
                          <p className="text-[var(--text-muted)] text-sm">{event.direccion}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!event.activo && (
                            <span className="text-xs bg-[var(--error)] text-white px-2 py-1">
                              Inactivo
                            </span>
                          )}
                          {event.activo && !event.entradas_activas && (
                            <span className="text-xs bg-orange-500 text-white px-2 py-1">
                              QRs Desactivados
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="text-sm flex gap-4">
                          <div>
                            <span className="text-[var(--accent-cyan)]">{event.reservations_count}</span>
                            <span className="text-[var(--text-muted)]"> reservas</span>
                          </div>
                          <div>
                            <span className="text-[var(--success)]">{event.checked_in_count || 0}</span>
                            <span className="text-[var(--text-muted)]"> validadas</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {/* Toggle QR activation */}
                          <button 
                            onClick={async () => {
                              const newState = !event.entradas_activas;
                              const mensaje = newState ? null : prompt(
                                "Mensaje para mostrar al escanear (dejar vacío para mensaje por defecto):",
                                "Entrada caducada - Aforo completo"
                              );
                              try {
                                await axios.put(`${API}/events/${event.id}`, {
                                  entradas_activas: newState,
                                  mensaje_desactivacion: mensaje
                                }, {
                                  headers: { Authorization: `Basic ${credentials}` }
                                });
                                fetchEvents();
                              } catch (err) {
                                setError("Error al actualizar el estado de las entradas");
                              }
                            }}
                            className={`p-2 transition-colors ${
                              event.entradas_activas !== false
                                ? 'text-[var(--success)] hover:text-orange-500' 
                                : 'text-orange-500 hover:text-[var(--success)]'
                            }`}
                            title={event.entradas_activas !== false ? "Desactivar QRs" : "Activar QRs"}
                            data-testid={`toggle-qr-${event.id}`}
                          >
                            {event.entradas_activas !== false ? <Ticket size={18} /> : <XCircle size={18} />}
                          </button>
                          <button 
                            onClick={() => handleViewReservations(event)}
                            className="p-2 text-[var(--text-muted)] hover:text-white transition-colors"
                            title="Ver reservas"
                            data-testid={`view-reservations-${event.id}`}
                          >
                            <List size={18} />
                          </button>
                          {event.tramos_enabled && (
                            <button 
                              onClick={async () => {
                                if (window.confirm(`¿Descontar 20 entradas del tramo activo del evento "${event.nombre}"?`)) {
                                  try {
                                    const response = await axios.post(`${API}/events/${event.id}/tramos/descontar?cantidad=20`, {}, {
                                      headers: { Authorization: `Basic ${credentials}` }
                                    });
                                    const data = response.data;
                                    let msg = `✅ ${data.message}\n\nDisponibles en tramo: ${data.disponibles_restantes}`;
                                    if (data.tramo_agotado) {
                                      msg += `\n\n⚠️ ¡TRAMO AGOTADO!`;
                                      if (data.siguiente_tramo_activado) {
                                        msg += `\n✅ Se ha activado: ${data.siguiente_tramo_activado}`;
                                      }
                                    }
                                    alert(msg);
                                    fetchEvents();
                                  } catch (err) {
                                    setError(err.response?.data?.detail || "Error al descontar entradas");
                                  }
                                }
                              }}
                              className="p-2 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
                              title="Descontar 20 entradas del tramo activo"
                              data-testid={`descontar-tramo-${event.id}`}
                            >
                              <Users size={18} />
                            </button>
                          )}
                          <button 
                            onClick={() => window.open(`${BACKEND_URL}/api/admin/events/${event.id}/reservations/download`, '_blank')}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--accent-cyan)] transition-colors"
                            title="Descargar reservas"
                            data-testid={`download-reservations-${event.id}`}
                          >
                            <Download size={18} />
                          </button>
                          <button 
                            onClick={() => window.open(`${BACKEND_URL}/api/admin/events/${event.id}/checked-in/download`, '_blank')}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--success)] transition-colors"
                            title="Descargar asistentes"
                            data-testid={`download-checked-in-${event.id}`}
                          >
                            <CheckCircle size={18} />
                          </button>
                          <button 
                            onClick={async () => {
                              if (window.confirm(`⚠️ ¿Estás seguro de que quieres RESETEAR todos los contadores del evento "${event.nombre}"?\n\nEsto eliminará TODAS las reservas (${event.reservations_count || 0}) y validaciones (${event.checked_in_count || 0}).\n\nEsta acción NO se puede deshacer.`)) {
                                try {
                                  const response = await axios.delete(`${API}/events/${event.id}/reservations/reset`, {
                                    headers: { Authorization: `Basic ${credentials}` }
                                  });
                                  alert(`✅ ${response.data.message}`);
                                  fetchEvents();
                                } catch (err) {
                                  setError("Error al resetear las reservas");
                                }
                              }
                            }}
                            className="p-2 text-[var(--text-muted)] hover:text-orange-500 transition-colors"
                            title="Resetear contadores (eliminar todas las reservas)"
                            data-testid={`reset-reservations-${event.id}`}
                          >
                            <RotateCcw size={18} />
                          </button>
                          <button 
                            onClick={() => handleEdit(event)}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit size={18} />
                          </button>
                          {/* Toggle visibility - Hide/Show event from public */}
                          <button 
                            onClick={async () => {
                              const newState = !event.activo;
                              const action = newState ? 'mostrar' : 'ocultar';
                              if (window.confirm(`¿${newState ? 'Mostrar' : 'Ocultar'} el evento "${event.nombre}" ${newState ? 'al' : 'del'} público?`)) {
                                try {
                                  await axios.put(`${API}/events/${event.id}`, {
                                    activo: newState
                                  }, {
                                    headers: { Authorization: `Basic ${credentials}` }
                                  });
                                  fetchEvents();
                                } catch (err) {
                                  setError(`Error al ${action} el evento`);
                                }
                              }
                            }}
                            className={`p-2 transition-colors ${
                              event.activo
                                ? 'text-[var(--success)] hover:text-gray-500' 
                                : 'text-gray-500 hover:text-[var(--success)]'
                            }`}
                            title={event.activo ? "Ocultar del público" : "Mostrar al público"}
                            data-testid={`toggle-visibility-${event.id}`}
                          >
                            {event.activo ? <Eye size={18} /> : <EyeOff size={18} />}
                          </button>
                          {!isGestor && (
                            <button 
                              onClick={() => handleDelete(event.id)}
                              className="p-2 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                              data-testid={`delete-event-${event.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>

        {/* Reservations Modal */}
        {viewingReservations && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--surface)] max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
                <div>
                  <h3 className="font-heading text-xl text-white uppercase">
                    Reservas: {viewingReservations.nombre}
                  </h3>
                  <p className="text-[var(--text-muted)] text-sm">
                    {reservationsList.length} reservas • {viewingReservations.fecha}
                  </p>
                </div>
                <button 
                  onClick={handleCloseReservations}
                  className="p-2 text-[var(--text-muted)] hover:text-white"
                  data-testid="close-reservations-modal"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="overflow-auto flex-grow p-4">
                {loadingReservations ? (
                  <div className="text-center py-8">
                    <div className="spinner-large"></div>
                  </div>
                ) : reservationsList.length === 0 ? (
                  <div className="text-center py-8 text-[var(--text-muted)]">
                    No hay reservas para este evento
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[var(--text-muted)] text-xs uppercase border-b border-[var(--border-color)]">
                        <th className="pb-3 pr-4">Nombre</th>
                        <th className="pb-3 pr-4">Teléfono</th>
                        <th className="pb-3 pr-4">Email</th>
                        <th className="pb-3 pr-4">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservationsList.map((res) => (
                        <tr key={res.id} className="border-b border-[var(--border-color)] hover:bg-[var(--surface-highlight)]">
                          <td className="py-3 pr-4">
                            <span className="text-white font-medium">{res.nombre}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <a 
                              href={`tel:${res.telefono}`}
                              className="flex items-center gap-2 text-[var(--accent-cyan)] hover:underline"
                            >
                              <Phone size={14} />
                              {res.telefono || '-'}
                            </a>
                          </td>
                          <td className="py-3 pr-4">
                            <a 
                              href={`mailto:${res.email}`}
                              className="flex items-center gap-2 text-[var(--primary)] hover:underline"
                            >
                              <Mail size={14} />
                              {res.email}
                            </a>
                          </td>
                          <td className="py-3 pr-4">
                            {res.checked_in ? (
                              <span className="inline-flex items-center gap-1 text-[var(--success)] text-sm">
                                <CheckCircle size={14} />
                                Validada
                              </span>
                            ) : (
                              <span className="text-[var(--text-muted)] text-sm">Pendiente</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              
              <div className="p-4 border-t border-[var(--border-color)] flex gap-4">
                <button
                  onClick={() => window.open(`${BACKEND_URL}/api/admin/events/${viewingReservations.id}/reservations/download`, '_blank')}
                  className="btn-download text-sm"
                >
                  <Download size={16} className="inline mr-2" />
                  Descargar CSV
                </button>
                <button
                  onClick={handleCloseReservations}
                  className="btn-primary text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default EventsTab;
